// Core for `grid agent` (mergeManagedBlock, ensureClaudeImport, corrupted-block
// guard). The canonical bootstrap block content is the markdown source of truth
// at packages/cli/templates/AGENTS.md — this module reads + merges it, holding
// logic only. A hash tripwire test guards the block content — see
// agent-files.test.ts.
import { readFileSync, existsSync } from 'node:fs';
import { join, dirname } from 'node:path';
import { fileURLToPath } from 'node:url';

export const CLOUDGRID_BLOCK_START = '<!-- cloudgrid:start -->';
export const CLOUDGRID_BLOCK_END = '<!-- cloudgrid:end -->';

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

function getAgentBootstrapBlock(): string {
  const paths = [
    join(__dirname, '../templates/AGENTS.md'),     // relative to dist/
    join(__dirname, '../../templates/AGENTS.md'),  // relative to src/lib/
  ];
  for (const p of paths) {
    if (existsSync(p)) {
      return readFileSync(p, 'utf8').trim();
    }
  }
  throw new Error(`Could not find AGENTS.md template in paths: ${paths.join(', ')}`);
}

export const AGENT_BOOTSTRAP_BLOCK = getAgentBootstrapBlock();

export type MergeStatus = 'create' | 'append' | 'replace' | 'unchanged';

export class CorruptedBlockError extends Error {
  constructor(file = 'file') {
    super(
      `${file} has a corrupted CloudGrid block. Fix or remove the ` +
        `${CLOUDGRID_BLOCK_START} / ${CLOUDGRID_BLOCK_END} markers and re-run.`,
    );
    this.name = 'CorruptedBlockError';
  }
}

export function countBlockStarts(text: string): number {
  return text.split(CLOUDGRID_BLOCK_START).length - 1;
}

export function countBlockEnds(text: string): number {
  return text.split(CLOUDGRID_BLOCK_END).length - 1;
}

/** Merge our managed block into existing file content. */
export function mergeManagedBlock(
  existing: string | null,
  block: string,
): { content: string; status: MergeStatus } {
  if (existing == null || existing.trim() === '') {
    return { content: block + '\n', status: 'create' };
  }
  // Count-aware guard. An imbalance of START vs END markers means the file is
  // corrupted — this catches the xor cases (1 start / 0 end, 0 start / 1 end)
  // AND the subtler data-loss edge where indexOf-based replace would silently
  // drop content (e.g. 2 START / 1 END: the naive slice between the first START
  // and the first END deletes the user's second block).
  const starts = countBlockStarts(existing);
  const ends = countBlockEnds(existing);
  if (starts !== ends) {
    throw new CorruptedBlockError();
  }

  if (starts >= 1) {
    // Replace only the first well-formed block; leave all other bytes intact.
    const startIdx = existing.indexOf(CLOUDGRID_BLOCK_START);
    const endIdx = existing.indexOf(CLOUDGRID_BLOCK_END, startIdx);
    // END missing after this START, or END before START -> corrupted.
    if (endIdx === -1 || endIdx < startIdx) {
      throw new CorruptedBlockError();
    }
    // A second START nested inside the first block (before its END) is the same
    // data-loss class as the count imbalance — reject rather than silently drop.
    const nestedStart = existing.indexOf(CLOUDGRID_BLOCK_START, startIdx + CLOUDGRID_BLOCK_START.length);
    if (nestedStart !== -1 && nestedStart < endIdx) {
      throw new CorruptedBlockError();
    }
    const before = existing.slice(0, startIdx);
    const after = existing.slice(endIdx + CLOUDGRID_BLOCK_END.length);
    const content = before + block + after;
    return { content, status: content === existing ? 'unchanged' : 'replace' };
  }

  // Append below the user's content (separator matches the .mjs exactly).
  const sep = existing.endsWith('\n\n') ? '' : existing.endsWith('\n') ? '\n' : '\n\n';
  return { content: existing + sep + block + '\n', status: 'append' };
}

const CLAUDE_IMPORT = '@AGENTS.md';
const IMPORT_RE = /^@(\.\/)?AGENTS\.md$/;

/** Ensure a CLAUDE.md contains the `@AGENTS.md` import line. */
export function ensureClaudeImport(
  existing: string | null,
): { content: string; status: MergeStatus } {
  if (existing == null || existing.trim() === '') {
    return { content: `# CLAUDE.md\n\n${CLAUDE_IMPORT}\n`, status: 'create' };
  }
  const hasImport = existing.split(/\r?\n/).some((line) => IMPORT_RE.test(line.trim()));
  if (hasImport) return { content: existing, status: 'unchanged' };
  const sep = existing.endsWith('\n') ? '' : '\n';
  return { content: existing + sep + CLAUDE_IMPORT + '\n', status: 'append' };
}
